'use client';

import Script from 'next/script';
import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { Skeleton } from '@ironhack/design-system2/components/skeleton';

import config from '@/config';
import { usePageContext } from '@/contexts/page-context/use-page-context';

type GoogleCredentialResponse = {
  credential: string;
};

type Props = {
  onCredentialResponse: (response: GoogleCredentialResponse) => void;
} & BoxProps;

export const GoogleButton = (props: Props): ReactElement => {
  const { onCredentialResponse, ...boxProps } = props;
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const { locale } = usePageContext();

  const isButtonRendered =
    googleButtonRef.current && googleButtonRef.current.children.length > 0;
  const [showSkeleton, setShowSkeleton] = useState(!isButtonRendered);

  const handleScriptLoaded = useCallback(() => {
    if (!googleButtonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: config.google.clientId,
      callback: onCredentialResponse,
    });
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      locale,
      shape: 'pill',
      size: 'large',
      text: 'continue_with',
      theme: 'outline',
      width: googleButtonRef.current.clientWidth,
    });
    setShowSkeleton(false);
  }, [locale, onCredentialResponse, setShowSkeleton]);

  useEffect(() => {
    const isScriptLoaded = Boolean(window.google);
    if (isScriptLoaded && !isButtonRendered) {
      handleScriptLoaded();
    }
  }, [handleScriptLoaded, isButtonRendered]);

  return (
    <>
      <Script
        onLoad={handleScriptLoaded}
        src="https://accounts.google.com/gsi/client"
        strategy="lazyOnload"
      />
      <Box h="40px" pos="relative" w={['full', null, '288px']} {...boxProps}>
        {showSkeleton && (
          <Skeleton borderRadius="20px" h="full" pos="absolute" w="full" />
        )}
        <Box ref={googleButtonRef} />
      </Box>
    </>
  );
};
